import { Component, ErrorInfo, ReactNode, Suspense, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { History } from "history";
import { portfolioCompaniesUrl, portfolioUrl } from "./utils/navigation";
import { LoadingIndicator } from "./components-v2/LoadingIndicator";
import { ErrorPage } from "./ErrorPage";
import { toSupportedLanguage } from "./i18n/languages";
import LoginPage from "./Login/LoginPage";
import SsoReauthRequiredPage from "./Login/SsoReauthRequiredPage";
import {
  createSsoConnectedAlert,
  isSsoConnectedProvider,
  ssoConnectedKey,
} from "./Login/loginDomain";
import { changeLanguageAction } from "./i18n/i18nDomain";
import {
  useDispatch,
  useI18n,
  useRootStore,
  useTranslation,
  useUserPrefs,
  useUserStore,
} from "./stores/RootStoreContext";
import { TFunction } from "./i18n/tt";
import { FullScreenLayout } from "./components-v2/FullScreenLayout";
import { getFullName } from "./utils/Roles";
import { Dialogs } from "./dialogs/dialogComponents";
import { RoutesView } from "./routes/RoutesView";
import { loginPageTitle, pageTitle } from "./routes/pageTitleDomain";
import { Announcement } from "./announcement/announcementComponents";
import { CustomRoleForUser } from "./common/customRole";
import { Impersonation, impersonationReasonLabel, User } from "./types/UserInfo";
import { alertAction } from "./events/eventsDomain";
import { Box } from "./components-v2/Box";
import { initKeyboardShortcuts } from "./keyboard/keyboardShortcuts";
import { KeyboardShortcutsHelpDialog } from "./keyboard/KeyboardShortcutsHelpDialog";
import { isProductionEnvironment } from "./utils/environments";
import { applyTheme, resolveTheme } from "./theme/themeDomain";
import { isOk, Whitelabel } from "./common/common";
import { AcceptUserTermsPage } from "./termOfService/AcceptUserTermsPage";
import { requiresUserTermsAcceptance } from "./termOfService/acceptUserTermsDomain";
import { QuickSwitcher } from "./portfolio/search/QuickSwitcher";
import { QuickSwitcherContext } from "./portfolio/search/portfolioSearchDomain";
import { portfolioAllCompanies } from "./portfolio/portfolioDomain";

/*
 * Injected as a stylesheet rather than inline styles on <html> so the whitelabel colors also win
 * inside [data-theme] scopes (the dark palette and light paper islands redeclare --color-primary*).
 */
const whitelabelColorsCss = (whitelabel: Whitelabel): string => {
  const declarations = [
    ["--color-primary", whitelabel.primaryColor],
    ["--color-primary-contrast", whitelabel.primaryContrastColor],
    ["--color-primary-light", whitelabel.primaryLightColor],
    ["--color-primary-dark", whitelabel.primaryDarkColor],
  ]
    .map(([name, value]) => `${name}: ${value.replaceAll(/[;{}]/g, "")};`)
    .join(" ");
  return `:root, [data-theme] { ${declarations} }`;
};

type ErrorBoundaryProps = {
  t: TFunction;
  children: ReactNode;
  onUncaughtError: (error: Error, errorInfo: ErrorInfo) => void;
};

type ErrorBoundaryState = { error: Error | null };

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onUncaughtError(error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return <ErrorPage backUrl={portfolioUrl} />;
    }

    return this.props.children;
  }
}

type Props = {
  history: History;
  onUncaughtError: ErrorBoundaryProps["onUncaughtError"];
};

const App = observer(function App({ history, onUncaughtError }: Props) {
  const rootStore = useRootStore();
  const userStore = useUserStore();
  const userPrefs = useUserPrefs();
  const i18n = useI18n();
  const dispatch = useDispatch();
  const route = rootStore.route;
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  useEffect(() => {
    if (!isProductionEnvironment()) {
      initKeyboardShortcuts(rootStore, dispatch, () => setShowShortcutsHelp(true));
    }
  }, [rootStore, dispatch]);
  const missingAccess =
    !userStore.isUserLoading &&
    userStore.hasOnlyShareholderAccess &&
    !route?.data?.allowShareholder &&
    !route?.data?.allowAnonymous;
  const isLoginRequired =
    !userStore.isUserLoading && userStore.isLoginRequired && !route?.data?.allowAnonymous;
  const requiresSsoReauth =
    !userStore.isUserLoading &&
    !userStore.isLoginRequired &&
    userStore.requireSsoReauthentication &&
    !route?.data?.allowAnonymous;
  const requiresUserTerms =
    !isLoginRequired && !requiresSsoReauth && requiresUserTermsAcceptance(userStore.user, route);
  const isLoggedOn =
    userStore.isAuthenticated && !isLoginRequired && !requiresSsoReauth && !requiresUserTerms;
  const quickSwitcherContext: QuickSwitcherContext = useMemo(
    () => ({ portfolio: portfolioAllCompanies, dispatch, t: i18n.t, language: i18n.lang }),
    [dispatch, i18n],
  );
  const announcement = <Announcement userPrefs={userPrefs} announcement={rootStore.announcement} />;
  const title =
    isLoginRequired || requiresSsoReauth
      ? loginPageTitle(i18n.t)
      : pageTitle({
          route,
          companies: [
            userStore.companyInfo?.company,
            isOk(userStore.shareholderViewResult)
              ? userStore.shareholderViewResult.data.company
              : undefined,
          ],
          items: {
            shareholder: rootStore.shareholdersStore.shareholderDetailsStore.shareholderDetails,
            project: rootStore.openProject,
            archive: rootStore.archive,
          },
          t: i18n.t,
        });

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    if (userStore.user && userStore.isAuthenticated && !queryParams.has("lang")) {
      dispatch(changeLanguageAction(toSupportedLanguage(userStore.user.language)));
    }
  }, [userStore.user, userStore.isAuthenticated, dispatch, history]);

  useEffect(() => {
    if (userStore.isAuthenticated) {
      const provider = sessionStorage.getItem(ssoConnectedKey);
      if (isSsoConnectedProvider(provider)) {
        sessionStorage.removeItem(ssoConnectedKey);
        dispatch(alertAction(createSsoConnectedAlert(provider)));
      }
    }
  }, [userStore.isAuthenticated, dispatch]);

  useEffect(() => {
    const whitelabel = userStore.companyInfo?.company.whitelabel;
    if (!whitelabel) return;
    const style = document.createElement("style");
    style.textContent = whitelabelColorsCss(whitelabel);
    document.head.appendChild(style);
    return () => style.remove();
  }, [userStore.companyInfo?.company.whitelabel]);

  const themePreference = userPrefs?.theme;
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => applyTheme(resolveTheme(themePreference, media.matches));
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [themePreference]);

  useEffect(() => {
    if (missingAccess) {
      dispatch([{ type: "navigate", url: portfolioCompaniesUrl, replace: true }]);
    }
  }, [missingAccess, dispatch]);

  if (userStore.isUserLoading) {
    return <LoadingIndicator fullscreen />;
  }

  if (userStore.isLoadError) {
    return <ErrorPage backUrl={portfolioCompaniesUrl} header={announcement} />;
  }

  return (
    <Suspense fallback={<LoadingIndicator fullscreen />}>
      <ErrorBoundary onUncaughtError={onUncaughtError} t={i18n.t}>
        <FullScreenLayout
          appBar={
            <>
              <RolePreviewBanner
                isPreviewing={userStore.isPreviewingRole}
                role={userStore.userRole}
              />
              <ImpersonationBanner user={userStore.user} />
              {announcement}
            </>
          }
          centerPanelContainerProps={{ className: "relative" }}
          centerPanel={
            isLoginRequired ? (
              <LoginPage />
            ) : requiresSsoReauth ? (
              <SsoReauthRequiredPage />
            ) : requiresUserTerms ? (
              <AcceptUserTermsPage />
            ) : (
              <RoutesView route={route} />
            )
          }
        />
        {!isLoginRequired && !requiresSsoReauth && <Dialogs store={rootStore} />}
        {!isLoginRequired && !requiresSsoReauth && showShortcutsHelp && (
          <KeyboardShortcutsHelpDialog onClose={() => setShowShortcutsHelp(false)} />
        )}
        {isLoggedOn && (
          <QuickSwitcher state={rootStore.portfolioSearch} context={quickSwitcherContext} />
        )}
      </ErrorBoundary>
    </Suspense>
  );
});

function RolePreviewBanner({
  isPreviewing,
  role,
}: {
  isPreviewing: boolean;
  role: CustomRoleForUser;
}) {
  return isPreviewing ? (
    <div className={"bg-accent1 color-accent2-contrast p-2 text-center font-bold"}>
      You are previewing "{role.customRoleName}"
    </div>
  ) : null;
}

function ImpersonationBanner({ user }: { user: User | null }) {
  const { t } = useTranslation();

  const reason = (impersonation: Impersonation) => {
    switch (impersonation?.reason) {
      case undefined:
        return "";
      case "OTHER":
        return impersonation.details ?? "";
      default:
        return t(impersonationReasonLabel(impersonation.reason));
    }
  };

  const message = (impersonation: Impersonation) => {
    switch (impersonation?.type) {
      case "USER":
        return `You are impersonating ${getFullName(user)}`;
      case "ADMIN": {
        return `Temporary admin access to ${impersonation.companyName} for ${reason(impersonation)}`;
      }
    }
  };

  return user?.impersonation ? (
    <Box
      className={"bg-accent2 color-accent2-contrast"}
      alignItems="center"
      justifyContent="center"
      padding={"sm"}
      gap={"md"}
    >
      {message(user.impersonation)}
      <a
        href={"/api/superadmin/logout-impersonated-user"}
        className={"color-accent2-contrast visited:color-accent2-contrast"}
      >
        Back to superadmin
      </a>
    </Box>
  ) : null;
}

export default App;
